import { useQuery, useMutation, createConnectQueryKey, createQueryOptions } from '@connectrpc/connect-query'
import { useTransport } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { listMyOrganizations, getOrganization, listOrganizationUsers, addUserToOrganization, updateOrganizationUser, removeUserFromOrganization, listItems, getItem, createItem, updateItem, deleteItem, listTaxRates, archiveTaxRate, listDiscounts, getDiscount, createTaxRate, updateTaxRate, createDiscount, updateDiscount, createOrganization, getOrganizationOnboarding, updateOnboardingStep, skipOnboardingStep, listQuoteTemplates, createQuoteTemplate, updateQuoteTemplate, deleteQuoteTemplate, getQuoteTemplateByEventType, getMe, reorderFormItems, getQuotePreFill, getSeatUsage } from '../gen/cortado/v1/cortado-CortadoService_connectquery'
import { create } from '@bufbuild/protobuf'
import { ListMyOrganizationsRequestSchema, GetOrganizationRequestSchema, ListOrganizationUsersRequestSchema, ListOrganizationUsersResponseSchema, GetOrganizationOnboardingRequestSchema, type Organization, type OrganizationOnboardingStep } from '../gen/cortado/v1/organizations_pb'
import { GetMeResponseSchema } from '../gen/cortado/v1/users_pb'
import type { Transport } from '@connectrpc/connect'
import { connectTransport } from '../lib/connect-transport'
import { ListItemsRequestSchema, ListItemsResponseSchema, type ListItemsResponse, GetItemRequestSchema, GetItemResponseSchema, ListTaxRatesRequestSchema, ListDiscountsRequestSchema, ListDiscountsResponseSchema, GetDiscountResponseSchema, ListQuoteTemplatesRequestSchema, ListQuoteTemplatesResponseSchema, GetQuoteTemplateByEventTypeRequestSchema, GetQuotePreFillRequestSchema } from '../gen/cortado/v1/cortado_pb'
import { ItemKindEnum, type TipAllocationModeEnum } from '../gen/cortado/v1/settings_pb'
import { normalizeTipAllocationMode } from '../components/shared/financial-document/tip-allocation-mode'
import { authStore } from './auth.store'
import { localSettingsStore, localSettingsActions } from './local-settings.store'
import { queryClient } from '../integrations/tanstack-query/root-provider'
import { Sentry } from '../lib/sentry'
import { Store, useStore } from '@tanstack/react-store'
import { forgetShapes } from 'skullet'
import { useEffect } from 'react'
import { isDemoModeActive } from '../lib/demo-mode'
import {
  prepareOptimisticUpdate,
  rollbackOptimisticUpdate,
  type OptimisticContext,
} from '@/lib/query-utils'

interface OrganizationState {
  /** The currently selected organization ID - local UI state */
  selectedOrganizationId: string | null
}

export const organizationStore = new Store<OrganizationState>({
  selectedOrganizationId: null,
})

// Skeleton shapes remembered under one organization describe its pages, not
// the next one's. Forget them when the selection moves off an org; the first
// selection keeps whatever this session already measured.
let previousOrganizationId = organizationStore.state.selectedOrganizationId
organizationStore.subscribe(() => {
  const { selectedOrganizationId } = organizationStore.state
  if (previousOrganizationId !== null && selectedOrganizationId !== previousOrganizationId) forgetShapes()
  previousOrganizationId = selectedOrganizationId
})

/**
 * Query options for the ListMyOrganizations RPC, for use in route loaders
 * via queryClient.ensureQueryData/prefetchQuery. Uses the same
 * connectTransport singleton as useMyOrganizations so the cache key matches.
 */
export function listMyOrganizationsQueryOptions(transport: Transport = connectTransport) {
  return createQueryOptions(listMyOrganizations, create(ListMyOrganizationsRequestSchema, {}), { transport })
}

/**
 * Query options for the GetOrganization RPC, for use in route loaders via
 * queryClient.ensureQueryData/prefetchQuery. Uses the same connectTransport
 * singleton as useCurrentOrganization so the cache key matches.
 */
export function getOrganizationQueryOptions(id: string, transport: Transport = connectTransport) {
  return createQueryOptions(getOrganization, create(GetOrganizationRequestSchema, { id }), { transport })
}

/**
 * Query options for the ListOrganizationUsers RPC, for use in route loaders
 * / beforeLoad guards via queryClient.ensureQueryData. Uses the same
 * connectTransport singleton as useOrganizationUsers so the cache key
 * matches (and so role-mutation invalidation of this RPC covers it too).
 */
export function listOrganizationUsersQueryOptions(organizationId: string, transport: Transport = connectTransport) {
  return createQueryOptions(listOrganizationUsers, create(ListOrganizationUsersRequestSchema, { organizationId }), { transport })
}

/**
 * Selects a default organization from the given list if none is currently
 * selected — prefers a previously selected org persisted in localStorage,
 * falling back to the first organization.
 *
 * Shared by the _app route loader (primary path on cold load, so selection
 * happens before the layout mounts) and useMyOrganizations' effect (fallback
 * path if the org list changes after mount).
 */
export function selectDefaultOrganization(organizations: Organization[]) {
  if (organizations.length === 0 || organizationStore.state.selectedOrganizationId) return

  // Try to restore previously selected org from localStorage
  const storedOrgId = localSettingsStore.state.selectedOrganizationId
  const storedOrgExists = storedOrgId && organizations.some(org => org.id === storedOrgId)

  const orgIdToSelect = storedOrgExists ? storedOrgId : organizations[0].id

  organizationStore.setState((state) => ({
    ...state,
    selectedOrganizationId: orgIdToSelect,
  }))
  Sentry.setTag('organization_id', orgIdToSelect)
}

/** Query parameter the superadmin console's "Open in app" link carries. */
export const SUPPORT_ORGANIZATION_PARAM = 'supportOrg'

/**
 * Adopts the organization named by `?supportOrg=<id>` (a superadmin support
 * session opened from the admin console) as the persisted selection, then
 * strips the parameter from the address bar. Runs before
 * {@link seedSelectedOrganizationFromStorage}, so the id goes through the same
 * seed-then-reconcile path as any stored selection: if the caller has no
 * active session for it, ListMyOrganizations won't contain it and the
 * reconcile step drops it.
 *
 * Writing localStorage (rather than only the in-memory store) keeps the
 * choice across the sign-in redirect when the tab has no session yet.
 *
 * @returns the adopted organization id, or null if the URL carried none.
 */
export function adoptSupportOrganizationFromUrl(): string | null {
  if (typeof window === 'undefined') return null

  const url = new URL(window.location.href)
  const organizationId = url.searchParams.get(SUPPORT_ORGANIZATION_PARAM)
  if (!organizationId) return null

  url.searchParams.delete(SUPPORT_ORGANIZATION_PARAM)
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)

  organizationStore.setState((state) => ({
    ...state,
    selectedOrganizationId: null,
  }))
  localSettingsActions.setSelectedOrganizationId(organizationId)
  return organizationId
}

/**
 * Optimistically selects the org persisted in localStorage BEFORE
 * ListMyOrganizations has resolved, so org-scoped queries can join the first
 * request wave instead of waiting a network tier for the org list.
 *
 * The stored id is unverified — the caller MUST reconcile it against the real
 * org list once it arrives (see {@link clearStaleSelectedOrganization}),
 * because a user can be removed from an org or the org can be deleted.
 *
 * @returns the seeded organization id, or null if nothing was seeded.
 */
export function seedSelectedOrganizationFromStorage(): string | null {
  if (organizationStore.state.selectedOrganizationId) return organizationStore.state.selectedOrganizationId

  const storedOrgId = localSettingsStore.state.selectedOrganizationId
  if (!storedOrgId) return null

  organizationStore.setState((state) => ({
    ...state,
    selectedOrganizationId: storedOrgId,
  }))
  Sentry.setTag('organization_id', storedOrgId)
  return storedOrgId
}

/**
 * Undoes an optimistic seed that turned out to be stale (user removed from the
 * org, or the org was deleted). Drops both the in-memory selection and the
 * persisted value so selectDefaultOrganization picks a valid org instead of
 * restoring the same dead id on the next load.
 */
export function clearStaleSelectedOrganization() {
  organizationStore.setState((state) => ({
    ...state,
    selectedOrganizationId: null,
  }))
  localSettingsActions.setSelectedOrganizationId(null)
}

/**
 * Reconciles an optimistic seed (see {@link seedSelectedOrganizationFromStorage})
 * against the user's real membership list, then selects a default org. A stale
 * seed is cleared along with every cached query, since that data belongs to an
 * org the user can no longer reach. A valid seed keeps everything it fetched.
 */
export function reconcileSelectedOrganization(
  organizations: Organization[],
  seededOrganizationId: string | null,
  client: Pick<typeof queryClient, 'clear'>
) {
  const seedIsStale =
    !!seededOrganizationId && !organizations.some((org) => org.id === seededOrganizationId)
  if (seedIsStale) {
    clearStaleSelectedOrganization()
    client.clear()
  }
  selectDefaultOrganization(organizations)
}

/**
 * Hook to fetch and manage the current user's organizations
 * 
 * This hook:
 * - Automatically fetches organizations when authenticated
 * - Only runs when a valid session exists
 * - Uses Connect-Query for type-safe RPC calls
 * - Integrates with TanStack Query for caching
 * 
 * @returns Query result with organizations array, loading state, and error
 */
export function useMyOrganizations() {
  // Get current auth state reactively
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session
  const isDemo = isDemoModeActive()

  // Use Connect-Query to call ListMyOrganizations RPC
  const query = useQuery(
    listMyOrganizations,
    create(ListMyOrganizationsRequestSchema, {}),
    {
      enabled: isAuthenticated || isDemo, // Allow in demo mode
      select: (data) => data?.organizations || [],
    }
  )

  const organizations = query.data || []

  // Auto-select organization if none selected and organizations are available.
  // This is a fallback for cases where the org list changes after mount — on
  // cold load, selection normally already happened in the _app route loader
  // via selectDefaultOrganization before this component ever renders.
  useEffect(() => {
    selectDefaultOrganization(organizations)
  }, [organizations])

  return {
    organizations,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to fetch the currently selected organization
 *
 * This is the single source of truth for the current organization data.
 * Components should use this hook directly instead of accessing a store.
 *
 * This hook:
 * - Automatically fetches organization details when selectedOrganizationId changes
 * - Only runs when authenticated and an organization is selected
 * - Uses Connect-Query for type-safe RPC calls
 * - Uses 5-minute stale time for caching
 *
 * @returns Query result with current organization, loading state, and error
 */
export function useCurrentOrganization() {
  // Get current auth state and selected organization ID reactively
  const session = useStore(authStore, (state) => state.session)
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const isAuthenticated = !!session
  const isDemo = isDemoModeActive()
  const hasSelectedOrganization = !!selectedOrganizationId

  // Use Connect-Query to call GetOrganization RPC
  const query = useQuery(
    getOrganization,
    create(GetOrganizationRequestSchema, { id: selectedOrganizationId || '' }),
    {
      enabled: (isAuthenticated || isDemo) && hasSelectedOrganization, // Allow in demo mode
      select: (data) => data?.organization || null,
    }
  )

  return {
    currentOrganization: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to fetch and manage users in the currently selected organization
 *
 * This hook:
 * - Automatically fetches organization users when selectedOrganizationId changes
 * - Only runs when authenticated and an organization is selected
 * - Uses Connect-Query for type-safe RPC calls
 * - Uses 5-minute stale time for caching
 *
 * @returns Query result with organization users array, loading state, and error
 */
export function useOrganizationUsers() {
  // Get current auth state and selected organization ID reactively
  const session = useStore(authStore, (state) => state.session)
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const isAuthenticated = !!session
  const isDemo = isDemoModeActive()
  const hasSelectedOrganization = !!selectedOrganizationId

  // Use Connect-Query to call ListOrganizationUsers RPC
  const query = useQuery(
    listOrganizationUsers,
    create(ListOrganizationUsersRequestSchema, { organizationId: selectedOrganizationId || '' }),
    {
      enabled: (isAuthenticated || isDemo) && hasSelectedOrganization, // Allow in demo mode
      select: (data) => data?.users || [],
    }
  )

  return {
    users: query.data || [],
    isLoading: query.isLoading,
    hasLoaded: query.isSuccess || !!query.data,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

export const organizationActions = {
  setSelectedOrganization: (id: string) => {
    // No-op if switching to the same org we're already on.
    if (organizationStore.state.selectedOrganizationId === id) return

    // Cancel any in-flight queries scoped to the previous org BEFORE we change
    // state. Otherwise those fetches can resolve against the old org context
    // and trigger backend permission_denied errors (Sentry: CORTADO-BACKEND-76/77/90/91/8W-95).
    // Note: cancelQueries does not abort in-flight mutations; those may still
    // complete against the prior org context.
    queryClient.cancelQueries()

    // Drop all cached data so stale data from the old org doesn't briefly render
    // (mirrors signOut in auth.store.ts).
    queryClient.clear()

    organizationStore.setState((state) => ({
      ...state,
      selectedOrganizationId: id,
    }))
    // Persist to localStorage
    localSettingsActions.setSelectedOrganizationId(id)
    // Update Sentry tag for error attribution
    Sentry.setTag('organization_id', id)
  },
}

/**
 * Hook to fetch items for an organization
 *
 * This hook:
 * - Fetches all items for the specified organization
 * - Only runs when authenticated and organizationId is provided
 * - Uses Connect-Query for type-safe RPC calls
 * - Uses 5-minute stale time for caching
 *
 * @param organizationId - The organization ID to fetch items for
 * @returns Query result with items array, loading state, and error
 */
export function useListItems(organizationId: string) {
  // Get current auth state reactively
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session
  const hasOrganizationId = !!organizationId

  // Use Connect-Query to call ListItems RPC
  const query = useQuery(
    listItems,
    create(ListItemsRequestSchema, { organizationId }),
    {
      enabled: isAuthenticated && hasOrganizationId,
      select: (data) => (data?.items || []).filter(
        (item) => item.itemKind !== ItemKindEnum.TRANSPORTATION,
      ),
    }
  )

  return {
    items: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to fetch a single item by ID
 *
 * This hook:
 * - Fetches a single item by its ID
 * - Only runs when authenticated and itemId is provided
 * - Uses Connect-Query for type-safe RPC calls
 * - Uses 5-minute stale time for caching
 *
 * @param itemId - The item ID to fetch
 * @returns Query result with item, loading state, and error
 */
export function useGetItem(itemId: string) {
  // Get current auth state reactively
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session
  const hasItemId = !!itemId

  // Use Connect-Query to call GetItem RPC
  const query = useQuery(
    getItem,
    create(GetItemRequestSchema, { id: itemId }),
    {
      enabled: isAuthenticated && hasItemId,
      select: (data) => data?.item || null,
    }
  )

  return {
    item: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to create a new item
 *
 * This hook:
 * - Creates a new item for the organization
 * - Automatically invalidates the items list query on success
 * - Returns the mutation state and trigger function
 *
 * @returns Mutation object with mutateAsync function and state
 */
export function useCreateItem() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    createItem,
    {
      onSuccess: (response) => {
        const createdItem = response.item

        if (!createdItem) return

        // Invalidate all item list queries
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listItems,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  return { mutation }
}

/**
 * Hook to update an existing item
 *
 * This hook:
 * - Updates an existing item
 * - Uses setQueryData to update individual item cache immediately (no refetch needed)
 * - Invalidates item list queries to refetch with new data
 * - Returns the mutation state and trigger function
 *
 * @returns Mutation object with mutateAsync function and state
 */
export function useUpdateItem() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(updateItem, {
    onMutate: async (variables) => {
      // Cancel in-flight queries and snapshot for rollback
      const queryKey = createConnectQueryKey({
        schema: getItem,
        transport,
        input: { id: variables.id },
        cardinality: 'finite',
      })
      return await prepareOptimisticUpdate({ queryClient, queryKey })
    },

    onError: (_error, _variables, context) => {
      // Rollback on error
      rollbackOptimisticUpdate({ queryClient, context: context as OptimisticContext })
    },

    onSuccess: (response) => {
      const updatedItem = response.item
      if (!updatedItem) return

      // 1. Update individual item cache
      const queryKey = createConnectQueryKey({
        schema: getItem,
        transport,
        input: { id: updatedItem.id },
        cardinality: 'finite',
      })

      queryClient.setQueryData(
        queryKey,
        create(GetItemResponseSchema, { item: updatedItem })
      )

      // 2. Update ALL list caches instantly
      const listQueryFilter = createConnectQueryKey({
        schema: listItems,
        transport,
        cardinality: undefined,
      })

      const existingListQueries = queryClient.getQueriesData<{ items: typeof updatedItem[] }>({
        queryKey: listQueryFilter,
      })

      existingListQueries.forEach(([listKey, oldData]) => {
        if (!oldData?.items) return

        const updatedItems = oldData.items.map((item) =>
          item.id === updatedItem.id ? updatedItem : item
        )

        queryClient.setQueryData(
          listKey,
          create(ListItemsResponseSchema, { items: updatedItems })
        )
      })
    },

    onSettled: () => {
      // Always invalidate for server consistency
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listItems,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return { mutation }
}

/**
 * Hook to delete an item from the catalog
 *
 * This hook:
 * - Calls the DeleteItem RPC to remove an item
 * - Invalidates list caches to ensure UI updates
 */
export function useDeleteItem() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(deleteItem, {
    onSuccess: () => {
      // Invalidate list queries to refetch
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listItems,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return { mutation }
}

/**
 * Hook to fetch tax rates for an organization
 *
 * This hook:
 * - Fetches all tax rates for the specified organization
 * - Only runs when authenticated and organizationId is provided
 * - Uses Connect-Query for type-safe RPC calls
 * - Uses 5-minute stale time for caching
 *
 * @param organizationId - The organization ID to fetch tax rates for
 * @returns Query result with tax rates array, loading state, and error
 */
export function useListTaxRates(organizationId: string) {
  // Get current auth state reactively
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session
  const hasOrganizationId = !!organizationId

  // Use Connect-Query to call ListTaxRates RPC
  const query = useQuery(
    listTaxRates,
    create(ListTaxRatesRequestSchema, { organizationId }),
    {
      enabled: isAuthenticated && hasOrganizationId,
      select: (data) => data?.taxRates || [],
    }
  )

  return {
    taxRates: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to fetch discounts for an organization
 *
 * This hook:
 * - Fetches all discounts for the specified organization
 * - Only runs when authenticated and organizationId is provided
 * - Uses Connect-Query for type-safe RPC calls
 * - Uses 5-minute stale time for caching
 *
 * @param organizationId - The organization ID to fetch discounts for
 * @returns Query result with discounts array, loading state, and error
 */
export function useListDiscounts(organizationId: string) {
  // Get current auth state reactively
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session
  const hasOrganizationId = !!organizationId

  // Use Connect-Query to call ListDiscounts RPC
  const query = useQuery(
    listDiscounts,
    create(ListDiscountsRequestSchema, { organizationId }),
    {
      enabled: isAuthenticated && hasOrganizationId,
      select: (data) => data?.discounts || [],
    }
  )

  return {
    discounts: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook for creating a tax rate
 * Invalidates tax rate list queries on success
 */
export function useCreateTaxRate() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    createTaxRate,
    {
      onSuccess: (response) => {
        const createdTaxRate = response.taxRate

        if (!createdTaxRate) return

        // Invalidate all tax rate list queries
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listTaxRates,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook for updating a tax rate's name and tax_type.
 * Percentage is immutable on the backend and must NOT be included in the FieldMask.
 */
export function useUpdateTaxRate() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(updateTaxRate, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listTaxRates,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook for archiving a tax rate (soft delete)
 * Invalidates tax rate list queries on success
 */
export function useArchiveTaxRate() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(archiveTaxRate, {
    onSuccess: () => {
      // Invalidate tax rate list queries to refetch
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listTaxRates,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return { mutation }
}

/**
 * Hook for creating a discount
 * Invalidates discount list queries on success
 */
export function useCreateDiscount() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    createDiscount,
    {
      onSuccess: (response) => {
        const createdDiscount = response.discount

        if (!createdDiscount) return

        // Invalidate all discount list queries
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listDiscounts,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export function useUpdateDiscount() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(updateDiscount, {
    onMutate: async (variables) => {
      // Cancel in-flight queries and snapshot for rollback
      const queryKey = createConnectQueryKey({
        schema: getDiscount,
        transport,
        input: { id: variables.id },
        cardinality: 'finite',
      })
      return await prepareOptimisticUpdate({ queryClient, queryKey })
    },

    onError: (_error, _variables, context) => {
      // Rollback on error
      rollbackOptimisticUpdate({ queryClient, context: context as OptimisticContext })
    },

    onSuccess: (response) => {
      const updatedDiscount = response.discount
      if (!updatedDiscount) return

      // 1. Update individual discount cache
      const queryKey = createConnectQueryKey({
        schema: getDiscount,
        transport,
        input: { id: updatedDiscount.id },
        cardinality: 'finite',
      })

      queryClient.setQueryData(
        queryKey,
        create(GetDiscountResponseSchema, { discount: updatedDiscount })
      )

      // 2. Update ALL list caches instantly
      const listQueryFilter = createConnectQueryKey({
        schema: listDiscounts,
        transport,
        cardinality: undefined,
      })

      const existingListQueries = queryClient.getQueriesData<{ discounts: typeof updatedDiscount[] }>({
        queryKey: listQueryFilter,
      })

      existingListQueries.forEach(([listKey, oldData]) => {
        if (!oldData?.discounts) return

        const updatedDiscounts = oldData.discounts.map((discount) =>
          discount.id === updatedDiscount.id ? updatedDiscount : discount
        )

        queryClient.setQueryData(
          listKey,
          create(ListDiscountsResponseSchema, { discounts: updatedDiscounts })
        )
      })
    },

    onSettled: () => {
      // Always invalidate for server consistency
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listDiscounts,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to get enforced tip percentage for a specific event type
 * Returns the tip percentage and whether an enforced tip exists for this event type
 *
 * Searches through the organization's enforced tips array to find one matching
 * the given event type.
 */
export function useEnforcedTipForEventType(
  eventType: string | undefined
) {
  const { currentOrganization } = useCurrentOrganization()

  // Find the enforced tip matching this event type from the array
  const enforcedTips = currentOrganization?.enforcedTips || []
  const matchingTip = eventType
    ? enforcedTips.find((tip) => tip.eventTypeKey === eventType)
    : undefined

  return {
    percentage: matchingTip?.percentage || 0,
    hasEnforcedTip: !!matchingTip,
  }
}

/**
 * The org-wide default tip collection mode used to seed new quotes and invoices.
 * Unset (or never customized) is LAST_SCHEDULE.
 */
export function useOrgTipAllocationModeDefault(): TipAllocationModeEnum {
  const { currentOrganization } = useCurrentOrganization()
  return normalizeTipAllocationMode(currentOrganization?.tipSelectorSettings?.defaultTipAllocationMode)
}

/**
 * Hook to add a user to an organization
 *
 * This hook:
 * - Adds a user to the organization by email (creates user if doesn't exist)
 * - Updates the organization users list cache immediately
 * - Invalidates queries for background refetch consistency
 * - Returns the mutation state and trigger function
 *
 * @returns Mutation object with mutateAsync function and state
 */
export function useAddUserToOrganization() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    addUserToOrganization,
    {
      onSuccess: (response, variables) => {
        const addedUser = response.user

        if (!addedUser) return

        // Adding a member changes seat usage - refresh the seat usage card
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getSeatUsage,
            transport,
            input: { organizationId: variables.organizationId },
            cardinality: 'finite',
          }),
        })

        // Update ALL list caches instantly by adding the new user
        const listQueryFilter = createConnectQueryKey({
          schema: listOrganizationUsers,
          transport,
          cardinality: undefined,
        })

        // Get all active list queries
        const existingListQueries = queryClient.getQueriesData<{ users: typeof addedUser[] }>({
          queryKey: listQueryFilter,
        })

        // Add the new user to each list
        existingListQueries.forEach(([queryKey, oldData]) => {
          if (!oldData?.users) return

          // Add new user to the list
          const updatedUsers = [...oldData.users, addedUser]

          // Set updated list back to cache
          queryClient.setQueryData(
            queryKey,
            create(ListOrganizationUsersResponseSchema, {
              users: updatedUsers,
            })
          )
        })

        // Background refetch for server consistency
        queryClient.invalidateQueries({
          queryKey: listQueryFilter,
        })
      },
    }
  )

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to update an organization user
 *
 * This hook:
 * - Updates an existing organization user's title and/or roles
 * - Updates ALL list caches instantly with the new user data
 * - Invalidates queries for background refetch consistency
 * - Returns the mutation state and trigger function
 *
 * @returns Mutation object with mutateAsync function and state
 */
export function useUpdateOrganizationUser() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    updateOrganizationUser,
    {
      onSuccess: (response, variables) => {
        const updatedUser = response.user

        if (!updatedUser) return

        // Changing a user's type (Admin/Event Staff) changes seat usage -
        // refresh the seat usage card
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getSeatUsage,
            transport,
            input: { organizationId: variables.organizationId },
            cardinality: 'finite',
          }),
        })

        // Update ALL list caches instantly
        const listQueryFilter = createConnectQueryKey({
          schema: listOrganizationUsers,
          transport,
          cardinality: undefined,
        })

        // Get all active list queries
        const existingListQueries = queryClient.getQueriesData<{ users: typeof updatedUser[] }>({
          queryKey: listQueryFilter,
        })

        // Update each list with the modified user
        existingListQueries.forEach(([queryKey, oldData]) => {
          if (!oldData?.users) return

          // Immutably update the user in this list
          const updatedUsers = oldData.users.map((user) =>
            user.user?.id === updatedUser.user?.id ? updatedUser : user
          )

          // Set updated list back to cache
          queryClient.setQueryData(
            queryKey,
            create(ListOrganizationUsersResponseSchema, {
              users: updatedUsers,
            })
          )
        })

        // Background refetch for server consistency
        queryClient.invalidateQueries({
          queryKey: listQueryFilter,
        })
      },
    }
  )

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to remove a user from an organization
 *
 * This hook:
 * - Removes a user from the organization
 * - Updates ALL list caches instantly by removing the user
 * - Invalidates queries for background refetch consistency
 * - Returns the mutation state and trigger function
 *
 * @returns Mutation object with mutateAsync function and state
 */
export function useRemoveUserFromOrganization() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    removeUserFromOrganization,
    {
      onSuccess: (_, variables) => {
        // variables contains the request with userId we removed
        const removedUserId = variables.userId

        if (!removedUserId) return

        // Removing a member changes seat usage - refresh the seat usage card
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getSeatUsage,
            transport,
            input: { organizationId: variables.organizationId },
            cardinality: 'finite',
          }),
        })

        // Update ALL list caches instantly by removing the user
        const listQueryFilter = createConnectQueryKey({
          schema: listOrganizationUsers,
          transport,
          cardinality: undefined,
        })

        // Get all active list queries
        const existingListQueries = queryClient.getQueriesData<{ users: Array<{ user?: { id?: string } }> }>({
          queryKey: listQueryFilter,
        })

        // Remove the user from each list
        existingListQueries.forEach(([queryKey, oldData]) => {
          if (!oldData?.users) return

          // Filter out the removed user
          const updatedUsers = oldData.users.filter(
            (user) => user.user?.id !== removedUserId
          )

          // Set updated list back to cache
          queryClient.setQueryData(
            queryKey,
            create(ListOrganizationUsersResponseSchema, {
              users: updatedUsers,
            })
          )
        })

        // Background refetch for server consistency
        queryClient.invalidateQueries({
          queryKey: listQueryFilter,
        })
      },
    }
  )

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to create a new organization
 *
 * This hook:
 * - Creates a new organization and adds the current user as admin
 * - Invalidates the organization list to refetch
 * - Auto-selects the new organization
 *
 * @returns Mutation for creating an organization
 */
export function useCreateOrganization() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(createOrganization, {
    onSuccess: (response) => {
      if (!response.organization) return

      // Persist so a wizard reload can seed the org (onboarding is outside `_app`).
      const createdOrganizationId = response.organization.id
      organizationStore.setState((state) => ({
        ...state,
        selectedOrganizationId: createdOrganizationId,
        currentOrganization: response.organization,
      }))
      localSettingsActions.setSelectedOrganizationId(createdOrganizationId)
      Sentry.setTag('organization_id', createdOrganizationId)

      // Invalidate organizations list to include the new org
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listMyOrganizations,
          transport,
          cardinality: undefined,
        }),
      })

      // Update getMe cache to reflect hasOrganization: true so that the _app.tsx
      // route guard doesn't redirect back to onboarding when navigating to the dashboard.
      // getMe has a 5-minute stale time, so without this update the guard would see
      // hasOrganization: false and loop the user back to the onboarding page.
      const getMeQueryKey = createConnectQueryKey({
        schema: getMe,
        transport,
        input: {},
        cardinality: 'finite',
      })
      const existingMe = queryClient.getQueryData<{ user: unknown; profileComplete: boolean }>(getMeQueryKey)
      if (existingMe) {
        queryClient.setQueryData(
          getMeQueryKey,
          create(GetMeResponseSchema, {
            user: (existingMe as any).user,
            profileComplete: (existingMe as any).profileComplete,
            hasOrganization: true,
          })
        )
      }
    },
  })

  return {
    createOrg: mutation.mutate,
    createOrgAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    data: mutation.data,
  }
}

// Stable fallback so callers that build derived state (e.g. useMemo) from
// `steps` don't see a new array identity on every render when there is no
// onboarding data yet.
const EMPTY_ONBOARDING_STEPS: OrganizationOnboardingStep[] = []

/**
 * Hook to get organization onboarding status
 *
 * @param organizationId - The organization ID to fetch onboarding for
 * @returns Query result with onboarding status
 */
export function useOrganizationOnboarding(organizationId: string | null) {
  const { session } = authStore.state
  const isAuthenticated = !!session

  const query = useQuery(
    getOrganizationOnboarding,
    create(GetOrganizationOnboardingRequestSchema, { organizationId: organizationId || '' }),
    {
      enabled: isAuthenticated && !!organizationId,
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  )

  return {
    onboarding: query.data?.onboarding || null,
    steps: query.data?.onboarding?.steps || EMPTY_ONBOARDING_STEPS,
    completedSteps: query.data?.onboarding?.completedSteps || 0,
    totalSteps: query.data?.onboarding?.totalSteps || 0,
    isCompleted: query.data?.onboarding?.isCompleted || false,
    completionRate: query.data?.onboarding?.completionRate || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to update an onboarding step
 *
 * @returns Mutation for updating onboarding step
 */
export function useUpdateOnboardingStep() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(updateOnboardingStep, {
    onSuccess: (_response, variables) => {
      // Invalidate onboarding query to refetch
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getOrganizationOnboarding,
          transport,
          input: { organizationId: variables.organizationId },
          cardinality: 'finite',
        }),
      })
    },
  })

  return {
    updateStep: mutation.mutate,
    updateStepAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error,
  }
}

/**
 * Hook to skip an onboarding step
 *
 * The server refuses required steps, so callers should only offer skip when
 * the step's `skippable` flag is set.
 *
 * @returns Mutation for skipping an onboarding step
 */
export function useSkipOnboardingStep() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(skipOnboardingStep, {
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getOrganizationOnboarding,
          transport,
          input: { organizationId: variables.organizationId },
          cardinality: 'finite',
        }),
      })
    },
  })

  return {
    skipStep: mutation.mutate,
    skipStepAsync: mutation.mutateAsync,
    isSkipping: mutation.isPending,
    error: mutation.error,
  }
}

/**
 * Hook to fetch quote templates for an organization
 *
 * This hook:
 * - Fetches all quote templates for the specified organization
 * - Only runs when authenticated and organizationId is provided
 * - Uses Connect-Query for type-safe RPC calls
 * - Uses 5-minute stale time for caching
 *
 * @param organizationId - The organization ID to fetch quote templates for
 * @returns Query result with quote templates array, loading state, and error
 */
export function useListQuoteTemplates(organizationId: string) {
  // Get current auth state reactively
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session
  const hasOrganizationId = !!organizationId

  // Use Connect-Query to call ListQuoteTemplates RPC
  const query = useQuery(
    listQuoteTemplates,
    create(ListQuoteTemplatesRequestSchema, { organizationId }),
    {
      enabled: isAuthenticated && hasOrganizationId,
      select: (data) => data?.quoteTemplates || [],
    }
  )

  return {
    quoteTemplates: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook for creating a quote template
 * Invalidates quote template list queries on success
 */
export function useCreateQuoteTemplate() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(createQuoteTemplate, {
    onSuccess: (response) => {
      const createdTemplate = response.quoteTemplate

      if (!createdTemplate) return

      // Invalidate all quote template list queries
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listQuoteTemplates,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook for updating a quote template
 * Updates list caches and invalidates for server consistency
 */
export function useUpdateQuoteTemplate() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(updateQuoteTemplate, {
    onSuccess: (response) => {
      const updatedTemplate = response.quoteTemplate
      if (!updatedTemplate) return

      // Update ALL list caches instantly
      const listQueryFilter = createConnectQueryKey({
        schema: listQuoteTemplates,
        transport,
        cardinality: undefined,
      })

      const existingListQueries = queryClient.getQueriesData<{ quoteTemplates: typeof updatedTemplate[] }>({
        queryKey: listQueryFilter,
      })

      existingListQueries.forEach(([listKey, oldData]) => {
        if (!oldData?.quoteTemplates) return

        const updatedTemplates = oldData.quoteTemplates.map((template) =>
          template.id === updatedTemplate.id ? updatedTemplate : template
        )

        queryClient.setQueryData(
          listKey,
          create(ListQuoteTemplatesResponseSchema, { quoteTemplates: updatedTemplates })
        )
      })
    },

    onSettled: () => {
      // Always invalidate for server consistency
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listQuoteTemplates,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook for deleting a quote template
 * Removes from list caches and invalidates for server consistency
 */
export function useDeleteQuoteTemplate() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(deleteQuoteTemplate, {
    onSuccess: (_, variables) => {
      const deletedId = variables.id

      if (!deletedId) return

      // Update ALL list caches instantly by removing the template
      const listQueryFilter = createConnectQueryKey({
        schema: listQuoteTemplates,
        transport,
        cardinality: undefined,
      })

      const existingListQueries = queryClient.getQueriesData<{ quoteTemplates: Array<{ id?: string }> }>({
        queryKey: listQueryFilter,
      })

      existingListQueries.forEach(([listKey, oldData]) => {
        if (!oldData?.quoteTemplates) return

        const updatedTemplates = oldData.quoteTemplates.filter(
          (template) => template.id !== deletedId
        )

        queryClient.setQueryData(
          listKey,
          create(ListQuoteTemplatesResponseSchema, { quoteTemplates: updatedTemplates })
        )
      })

      // Background refetch for server consistency
      queryClient.invalidateQueries({
        queryKey: listQueryFilter,
      })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to fetch a quote template by event type
 *
 * This hook:
 * - Fetches the quote template for a specific event type
 * - Only runs when authenticated, organizationId is provided, and eventType is defined
 * - Returns null if no template exists (not an error)
 *
 * @param organizationId - The organization ID to fetch the template for
 * @param eventType - The event type to fetch the template for (undefined disables the query)
 * @returns Query result with quote template (or null), loading state
 */
export function useQuoteTemplateByEventType(
  organizationId: string,
  eventType: string | undefined
) {
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session
  const hasOrganizationId = !!organizationId
  const hasEventType = !!eventType

  const query = useQuery(
    getQuoteTemplateByEventType,
    create(GetQuoteTemplateByEventTypeRequestSchema, {
      organizationId,
      eventTypeKey: eventType || '',
    }),
    {
      enabled: isAuthenticated && hasOrganizationId && hasEventType,
      select: (data) => data?.quoteTemplate || null,
    }
  )

  return {
    quoteTemplate: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  }
}

/**
 * Hook to reorder form items
 *
 * This hook:
 * - Optimistically reorders items in the cache immediately (no visual snap-back)
 * - Rolls back on error
 * - Invalidates item list queries on success to sync with server order
 */
export function useReorderFormItems() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(reorderFormItems, {
    onMutate: async (variables) => {
      const listQueryKey = createConnectQueryKey({
        schema: listItems,
        transport,
        cardinality: undefined,
      })

      await queryClient.cancelQueries({ queryKey: listQueryKey })

      const previousQueries = queryClient.getQueriesData({ queryKey: listQueryKey })

      // Optimistically update formDisplayOrder in all list caches
      previousQueries.forEach(([key, data]) => {
        const typedData = data as ListItemsResponse | undefined
        if (!typedData?.items) return

        const itemIds = variables.itemIds ?? []
        if (!itemIds.length) return
        const orderMap = new Map(itemIds.map((id, index) => [id, index]))
        const reorderedItems = typedData.items.map((item) => {
          const newOrder = orderMap.get(item.id)
          // Items not in itemIds (includeOnForm === false) are unchanged
          return newOrder !== undefined ? { ...item, formDisplayOrder: newOrder } : item
        })

        queryClient.setQueryData(
          key,
          create(ListItemsResponseSchema, { items: reorderedItems })
        )
      })

      return { previousQueries }
    },

    onError: (_error, _variables, context) => {
      // Rollback all list caches to their pre-mutation state
      const { previousQueries } = context as { previousQueries: Array<[unknown[], ListItemsResponse | undefined]> }
      previousQueries?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
    },

    onSuccess: () => {
      // Refetch to sync with server-assigned formDisplayOrder values
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listItems,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

const EMPTY_EVENT_GROUPS: never[] = []
const EMPTY_GENERAL_ITEMS: never[] = []
const EMPTY_QUOTE_EVENTS: never[] = []
const EMPTY_PREFILL_LINE_ITEMS: never[] = []

export function useGetQuotePreFill(inquiryId: string, quoteTemplateId?: string) {
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session
  const hasInquiryId = !!inquiryId

  const query = useQuery(
    getQuotePreFill,
    create(GetQuotePreFillRequestSchema, { inquiryId, quoteTemplateId }),
    {
      enabled: isAuthenticated && hasInquiryId,
    }
  )

  return {
    quoteTitle: query.data?.quoteTitle,
    quoteDescription: query.data?.quoteDescription,
    eventGroups: query.data?.eventGroups ?? EMPTY_EVENT_GROUPS,
    generalItems: query.data?.generalItems ?? EMPTY_GENERAL_ITEMS,
    quoteEvents: query.data?.quoteEvents ?? EMPTY_QUOTE_EVENTS,
    /** Every starting line in order (event groups' lines, then general items). */
    lineItems: query.data?.lineItems ?? EMPTY_PREFILL_LINE_ITEMS,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
